import {logger, Time, TimeInterval, Util} from 'yougile-util'
import {
  DataObjectId,
  SandboxTree,
  StickerStateId,
  Tag,
  TimerStickerValue
} from 'yougile-model'
import {globals} from '../../extensions/globals-base'
import {Sticker, stickerCtl} from '../../extensions/sticker/sticker'
import {Item} from './item'
import {Items} from './items'

/**
 * @name=sticker variable
 * @code
 * Stickers.get('... sticker name');
 * @description
 * Represents a sticker. You can get this variable from Stickers.get
 * function.
 * @descriptionRu
 * Переменная, представляющая стикер. Можно получить переменную со стикером
 * вызвав функцию Stickers.get(...)
 * @example
 * var st = Stickers.get('Priority');
 * // st is a sticker variable now
 */
export class StickerObj {
  public id: DataObjectId
  public box: SandboxTree /* api:exclude */

  constructor(id: DataObjectId, box: SandboxTree) {
    this.id = id
    this.box = box
  }

  /**
   * @code
   * Stickers.get('... sticker’s name').name;
   * @description
   * Return a name of a sticker.
   * @descriptionRu
   * Возвращает имя стикера.
   */
  public get name(): string {
    const tag = this.box.get(this.id) as Tag
    return tag.title
  }

  public set name(value: string) {
    const tag = this.box.get(this.id) as Tag
    if (Sticker.getController(tag)?.isSystem) {
      logger.warn('Can’t change name of system sticker')
      return
    }
    this.box.editTitle(tag, value)
  }

  /**
   * @code
   * Stickers.get('... sticker’s name').type;
   * @description
   * Returns a type of a sticker. Possible types are: Text, Sprint, Deadline,
   * Timer, User.
   * @descriptionRu
   * Возвращает тип стикера. Возможные типы: Text, Sprint, Deadline,
   * Timer, User.
   */
  public get type(): string {
    const tag = this.box.get(this.id) as Tag
    const type = Sticker.getType(tag)
    return type[0].toUpperCase() + type.slice(1)
  }

  /**
   * @code
   * Stickers.get('... sticker name here').states;
   * @description
   * Returns an array of all sticker states. Sticker state is a string that is
   * specified when sticker is added to a task. For example if there is
   * Priority sticker then its states can be
   * 'Major', 'Normal', 'Minor', … and tasks that
   * have this sticker are not only have it pinned but also have a specific
   * state set.
   * @descriptionRu
   * Возвращает массив всех состояний стикера. Состояние стикера
   * задаётся строкой с его именем, к состоянию также может быть
   * прикреплена дополнительная информация (напр. цвет) эта
   * информация может быть получена через функцию .getData()
   * @example
   * var priority = Stickers.get('Priority');
   * var states = priority.states;
   * var firstState = states[0]; // 'Minor'
   * var color = priority.getData(firstState).color;
   */
  public get states(): string[] {
    const tag = this.box.get(this.id) as Tag
    const type = Sticker.getType(tag)
    if (type === 'user') {
      return this.box.getAllUsers(false).map(u => u.id)
    } else if (type !== 'string' && type !== 'sprint') {
      return []
    } else {
      return tag.states.order.map(id => tag.states.index[id].name)
    }
  }

  /**
   * @code
   * Sticker.get('... sticker’s name').addState('... state name');
   * @example
   * var s = Stickers.get('Priority');
   * // adding a new state to “Priority” sticker
   * s.addState('Super important!');
   *
   * // now we also can add a color to this new state
   * s.setData('Super important!', '#f00'); // let it be red for example
   * @description
   * Adds a state to the sticker.
   */
  public addState(name: string): void {
    const tag = this.box.get(this.id) as Tag
    if (Sticker.getType(tag) !== 'string') {
      logger.error('Can create states only for “String” sticker')
      return
    }
    const ctl = stickerCtl.String
    ctl.addState(tag, {id: ctl.makeShortId(), name, color: 'sticker-gray'})
  }

  /**
   * @code
   * Sticker.get('... sticker name').removeState('??? state name');
   * @example
   * var s = Stickers.get('Priority');
   * // suppose, we don’t want this “Critical” state anymore
   * s.removeState('Critical');
   * @description
   * Removes a state from the sticker.
   */
  public removeState(name: string): void {
    const tag = this.box.get(this.id) as Tag
    Object.keys(tag.states.index).forEach((id: StickerStateId) => {
      const state = tag.states.index[id]
      if (state.name === name) {
        stickerCtl.String.removeState(tag, id)
      }
    })
  }

  /**
   * @code
   * Stickers.get('... sticker name here').getData('??? sticker state');
   * @description
   * Returns an additional data, associated with the sticker state
   * (for Text sticker it is color, for sprint sticker it is dates).
   * @descriptionRu
   * Возвращает дополнительные данные, связанные со значением стикера.
   * (для текстового стикера – цвет, для стикера Спринт – даты).
   */
  public getData(name: string): unknown {
    const tag = this.box.get(this.id) as Tag
    return Object.keys(tag.states.index)
      .map(id => tag.states.index[id])
      .find(s => s.name === name)
  }

  /**
   * @code
   * Stickers.get('... sticker name here').getData({});
   * @example
   * var st = Stickers.get('Priority');
   * // we want to change a color of “Normal” state to blue
   * st.setState('Normal', {color: '#00f'});
   * @description
   * Change data associated with the sticker state
   */
  public setData(name: string, data: unknown): void {
    if (typeof data !== 'object') {
      logger.error('The data is not an object. Can’t set it as sticker data')
      return
    }
    const tag = this.box.get(this.id) as Tag

    Object.keys(tag.states.index).forEach((id: StickerStateId) => {
      const state = tag.states.index[id]
      if (state.name === name) {
        const newData = {name, ...data}
        stickerCtl.String.changeState(tag, id, newData as any)
      }
    })
  }

  /**
   * @code
   * var task = Items.get('...');
   * Stickers.get('Timer|Stopwatch').getHistory(task);
   * @description
   * Gets a history of state changes from Timer or Stopwatch stickers
   * in some task.
   * @descriptionRu
   * Получает историю изменений состояний для стикеров Timer (таймер)
   * или Stopwatch (секундомер) в определённой задаче.
   * @example
   * var task = Items.get('...');
   * var timer = Stickers.get('Timer');
   * var hist = timer.getHistory(task);
   * // hist is an array of objects
   */
  public getHistory(task: Item): unknown[] {
    if (this.type !== 'Timer' && this.type !== 'Stopwatch') {
      throw Error(
        'Only Timer and Stopwatch stickers are allowed for .getHistory()'
      )
    }
    const hub = Items.toHub(task)
    const tag = this.box.get(this.id) as Tag
    if (!Sticker.isPinned(tag, hub)) {
      return []
    }
    const val = hub.stickers[tag.id] as TimerStickerValue
    if (!val) {
      return []
    }
    const hist = Util.clone(val.history || [])
    if (val.current) {
      hist.push(Util.clone(val.current))
    }
    return hist.map(ent => ({
      isRunning: ent.running,
      timeLeft: TimeInterval.fromString(ent.time),
      timestamp: ent.timestamp,
      userId: ent.by
    }))
  }

  /**
   * @code
   * var task = Items.get('...');
   * Stickers.get('Timer|Stopwatch').clearHistory(task);
   * @description
   * Remove the history of state changes of Timer or Stopwatch sticker
   * from the task.
   * @descriptionRu
   * Очищает историю изменений состояний для стикеров Timer (таймер)
   * или Stopwatch (секундомер) в определённой задаче.
   * @example
   * var task = Items.get('...');
   * var timer = Stickers.get('Timer');
   * var hist = timer.clearHistory(task);
   */
  public clearHistory(task: Item): void {
    if (this.type !== 'Timer' && this.type !== 'Stopwatch') {
      throw Error(
        'Only Timer and Stopwatch stickers are allowed for .clearHistory()'
      )
    }
    const hub = Items.toHub(task)
    const tag = this.box.get(this.id) as Tag
    if (!Sticker.isPinned(tag, hub)) {
      logger.warn('Sticker must be pinned to clear history of changes')
      return
    }
    const val = hub.stickers[tag.id] as TimerStickerValue
    if (val) {
      val.history = []
      Sticker.pin(tag, 'api', hub, val)
    } else {
      Sticker.pin(tag, 'api', hub)
    }
  }

  /**
   * @code
   * var task = Items.get('...');
   * Stickers.get('Timer|Stopwatch').changeTime(task, '2h30m');
   * @description
   * Sets a time for stickers Timer and Stopwatch.
   * @descriptionRu
   * Устанавливает время для стикеров Timer и Stopwatch.
   * @example
   * var task = Items.get('...');
   * var timer = Stickers.get('Timer');
   * timer.changeTime(task, '1d5h');
   */
  public changeTime(task: Item, time: string): void {
    if (this.type !== 'Timer' && this.type !== 'Stopwatch') {
      throw Error(
        'Only Timer and Stopwatch stickers are allowed for .setTime()'
      )
    }
    const hub = Items.toHub(task)
    const tag = this.box.get(this.id) as Tag
    if (!Sticker.isPinned(tag, hub)) {
      Sticker.pin(tag, 'api', hub, {
        time,
        timestamp: Time.now(),
        by: globals.currentUserId,
        running: true
      })
    } else {
      const newVal = hub.stickers[tag.id].current
      newVal.time = time
      Sticker.pin(tag, 'api', hub, newVal)
    }
  }

  /**
   * @code
   * var task = Items.get('...');
   * Stickers.get('Timer|Stopwatch').start(task);
   * @description
   * Starts Timer or Stopwatch on the specified task.
   * @descriptionRu
   * Запускает Timer или Stopwatch на указанной задаче.
   * @example
   * var task = Items.get('...');
   * var timer = Stickers.get('Timer');
   * timer.start(task, '1d');
   *
   * var stopwatch = Stickers.get('Stopwatch');
   * stopwatch.start(task);
   */
  public start(task: Item, time: string): void {
    if (this.type !== 'Timer' && this.type !== 'Stopwatch') {
      throw Error('Only Timer and Stopwatch stickers are allowed for .start()')
    }
    const hub = Items.toHub(task)
    const tag = this.box.get(this.id) as Tag
    if (!Sticker.isPinned(tag, hub)) {
      Sticker.pin(tag, 'api', hub, {
        time: time || '0s',
        timestamp: Time.now(),
        by: globals.currentUserId,
        running: true
      })
    } else {
      const val = hub.stickers[tag.id] as TimerStickerValue
      if (val.current.running) {
        return
      }
      const oldTi = TimeInterval.fromString(val.current.time)
      const newTi = TimeInterval.getActual(oldTi, val.current.timestamp)
      const newVal: TimerStickerValue = {
        current: {
          time: val.current.running ? newTi.toString() : oldTi.toString(),
          by: globals.currentUserId,
          timestamp: Time.now(),
          running: true
        },
        history: val.history
      }
      Sticker.pin(tag, 'api', hub, newVal)
    }
  }

  /**
   * @code
   * var task = Items.get('...');
   * Stickers.get('Timer|Stopwatch').stop(task);
   * @description
   * Stops Timer or Stopwatch on the specified task.
   * @descriptionRu
   * Останавливает Timer или Stopwatch на указанной задаче.
   * @example
   * var task = Items.get('...');
   * var timer = Stickers.get('Timer');
   * timer.stop(task);
   *
   * var stopwatch = Stickers.get('Stopwatch');
   * stopwatch.stop(task);
   */
  public stop(task: Item): void {
    if (this.type !== 'Timer' && this.type !== 'Stopwatch') {
      throw Error('Only Timer and Stopwatch stickers are allowed for .stop()')
    }
    const hub = Items.toHub(task)
    const tag = this.box.get(this.id) as Tag
    if (!Sticker.isPinned(tag, hub)) {
      Sticker.pin(tag, 'api', hub, {
        time: '0s',
        timestamp: Time.now(),
        by: globals.currentUserId,
        running: false
      })
    } else {
      const val = hub.stickers[tag.id] as TimerStickerValue
      if (!val.current.running) {
        return
      }
      const oldTi = TimeInterval.fromString(val.current.time)
      const newTi = TimeInterval.getActual(oldTi, val.current.timestamp)
      const newVal: TimerStickerValue = {
        current: {
          time: val.current.running ? newTi.toString() : oldTi.toString(),
          by: globals.currentUserId,
          timestamp: Time.now(),
          running: false
        },
        history: val.history
      }
      Sticker.pin(tag, 'api', hub, newVal)
    }
  }
}
